import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import ButtonDefault from "components/DesignComponents/ButtonDefault";
import { TooltipGeneral } from "components/DesignComponents/Tooltips/TooltipGeneral";
import { TOOLTIP_PLACEMENT } from "components/DesignComponents/Tooltips/types/enums";
import { RectangleLoader } from "shared/loaders/RectangleLoader";
import { ACTIVITY_STATUS } from "shared/types/enums";

import { LOADING } from "../../constants";
import { useColors } from "../hooks";
import { SingleLegendItemProps } from "./types";

const SingleLegendItem = ({
    statusName,
    stats,
    isLoading,
    isEmpty,
    hoveredSlice,
    onHover,
    onClick
}: SingleLegendItemProps) => {
    const { t } = useTranslation();

    const { gray900, statusColors, textDisabled, textPlaceholder } =
        useColors();

    const isDisabled = hoveredSlice !== null && hoveredSlice !== statusName;

    const bgColorRest =
        statusColors[statusName].bg[isDisabled ? "hover" : "rest"];

    const borderColorRest =
        statusColors[statusName].border[isDisabled ? "hover" : "rest"];

    const loaderColor = statusColors[LOADING].bg.rest;

    const isReady = !isLoading && !isEmpty;

    const getStatusCount = (status: ACTIVITY_STATUS) =>
        stats.find(({ name }) => name === status)?.count || 0;

    const handlePointerEnter = () => isReady && onHover(statusName);
    const handlePointerLeave = () => onHover(null);
    const handleClick = () => isReady && onClick(statusName);

    return (
        <TooltipGeneral
            title={isReady ? t("Dashboard##take me there") : ""}
            placement={TOOLTIP_PLACEMENT.Top}
        >
            <ButtonDefault
                onPointerEnter={handlePointerEnter}
                onPointerLeave={handlePointerLeave}
                onClick={handleClick}
                css={css({
                    display: "flex",
                    alignItems: "center",
                    columnGap: "8px",
                    cursor: isReady ? "pointer" : "default"
                })}
            >
                <div
                    css={css({
                        position: "relative",
                        width: "8px",
                        height: "32px",
                        boxSizing: "border-box",
                        borderRadius: "2px",
                        borderStyle: "solid",
                        borderWidth: "1px",
                        borderColor: borderColorRest,
                        backgroundColor: bgColorRest,
                        transition:
                            "background-color 0.3s ease, border-color 0.3s ease"
                    })}
                />

                <div
                    css={css({
                        fontSize: "14px",
                        fontWeight: "500",
                        lineHeight: "20px",
                        letterSpacing: "0.1px",

                        "& > div": {
                            transition: "color 0.3s ease"
                        },

                        "& > div:first-of-type": {
                            color: isDisabled ? textDisabled : gray900
                        },

                        "& > div:last-of-type": {
                            color: isDisabled ? textDisabled : textPlaceholder
                        }
                    })}
                >
                    {isLoading ? (
                        <RectangleLoader
                            width={40}
                            height={14}
                            backgroundColor={loaderColor}
                            borderRadius="2px"
                            customStyle={{ margin: "3px 0" }}
                        />
                    ) : (
                        <div>{getStatusCount(statusName)}</div>
                    )}
                    <div>{t(`General##${statusName}`)}</div>
                </div>
            </ButtonDefault>
        </TooltipGeneral>
    );
};

export default SingleLegendItem;
